import { Card, CardContent, IconButton, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import {
  Column,
  FormInput,
  HorizontalFormInput,
  StackedFormInput,
  Row,
  Spacer,
  StepsHeader,
  AddressSearchbox,
  DropdownMenu,
  ButtonFooter,
} from '../components';
import { isAxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppStorage, useMutation, MutationStatus, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { SignUpWizard, signUpWizardKey } from '../services/signUp';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { AddressInfo, OrganizationData } from '../models/OrganizationData';
import {
  SaveOrganizationData,
  UnselectOrganization,
  UpdateOrganizationData,
} from '../data/organization/mutations';
import { UpdateSignUpState, CreateSignUpState } from '../data/signUp/mutations';
import { formatPhoneNumber, trimPhoneNumber } from '../helpers/utilityFunctions';
import { useDeviceType } from '../hooks/useDeviceType';
import { OrganizationCompartments, organizationStorageKey } from '../data/organization';
import { ToastbarContext } from '../App';
import { Toast } from '../models/Toast';
import { Loading } from '../routes/AuthenticatedRoutes';
import { statesList } from '../helpers/constants';
import { UserCompartments, userStorageKey } from '../data/user';
import { UserRole } from '../models/UserData';
import { AdminTierTypeEnum, DrillDownWizard, drillDownWizardKey } from '../services/drillDown';
import { Svgs } from '../assets/svg';
import PricebookRestrictionsDrawer from '../dashboard/pricebook-restrictions/PricebookRestrictionsDrawer';
import { RestrictionMode } from '../dashboard/pricebook-restrictions/helpers/pricebookRestrictionUtils';
import {
  useOrgAvailablePricebooks,
  useTeamAvailablePricebooks,
  useAllPricebookBundles,
} from '../hooks/usePricebookOptions';
import {
  SaveOrgPricebookRestrictions,
  RevertOrgPricebookRestrictions,
} from '../data/pricebook/mutations';
import { SignUpCompartments, signUpStorageKey } from '../data/signUp';
import { OrgSignUpStateData } from '../models/SignUpData';
import { useLoading } from '../hooks/useLoading';
import { LicenseCompartments, licenseStorageKey } from '../data/license';
import { LicenseAgreementData } from '../api/apis/LicenseAgreementApi';
import { useCancelContractToast } from '../components/CancelContract';

const AboutYourOrg = () => {
  const appStorage = useAppStorage();
  const location = useLocation();
  const navigate = useNavigate();
  const { setToast } = useContext(ToastbarContext);
  const { isMobile, isTablet } = useDeviceType();
  const { setLoading } = useLoading();

  const saveOrganizationData = useMutation(new SaveOrganizationData());
  const updateOrganizationData = useMutation(new UpdateOrganizationData());
  const updateSignUpState = useMutation(new UpdateSignUpState());
  const createSignUpState = useMutation(new CreateSignUpState());
  const unselectOrganization = useMutation(new UnselectOrganization());
  const saveOrgRestrictions = useMutation(new SaveOrgPricebookRestrictions());
  const revertOrgRestrictions = useMutation(new RevertOrgPricebookRestrictions());
  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);

  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);

  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const currentOrganization = useObservable(
    organizationDataCache.observe$<OrganizationData>('organization'),
  );
  const userRoles = useObservable(userDataCache.observe$<UserRole[]>('userRoles'));
  const googlePlacesKey = useObservable(signUpWizard.googlePlacesKey$);
  const multipleProductTypesAvailable = useObservable(signUpWizard.multipleProductTypes$);
  const origin = useObservable(drillDownWizard.origin$);
  const highestRevenueAccess = useObservable(drillDownWizard.highestRevenueAccess$);
  const orgAvailablePricebooks = useOrgAvailablePricebooks();
  const teamAvailablePricebooks = useTeamAvailablePricebooks();
  const allPricebookBundles = useAllPricebookBundles();
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const inUsePricebookIds = useMemo(() => {
    if (!licenseAgreements) return new Set<number>();
    return new Set(licenseAgreements.filter(la => la.docStatus).map(la => la.pricebookId));
  }, [licenseAgreements]);

  const inUsePricebookToast = useCancelContractToast({
    message: 'Cannot remove a pricebook with an active contract.',
  });

  const validateInUsePricebooks = useCallback(
    (mode: RestrictionMode, selectedIds: number[], visibleIds: Set<number>) => {
      if (
        mode !== 'inherit' &&
        inUsePricebookIds.size > 0 &&
        [...inUsePricebookIds].some(id => visibleIds.has(id) && !selectedIds.includes(id))
      ) {
        inUsePricebookToast();
        return false;
      }
      return true;
    },
    [inUsePricebookIds, inUsePricebookToast],
  );

  const showGearIcon = highestRevenueAccess !== undefined;
  const [pricebookDrawerOpen, setPricebookDrawerOpen] = useState(false);
  const [restrictionSelections, setRestrictionSelections] = useState<{
    mode: RestrictionMode;
    pricebookIds: number[];
  } | null>(null);
  const back =
    origin === AdminTierTypeEnum.Organization
      ? '/drill-down/choose-organization'
      : '/controller/organizations';
  const organizationData = useMemo<OrganizationData | undefined>(() => {
    if (!currentOrganization || !orgSignUpState?.state?.orgData) return undefined;
    return JSON.parse(orgSignUpState.state.orgData);
  }, [currentOrganization, orgSignUpState?.state?.orgData]);
  const { activeStep, totalSteps } = signUpWizard.currentStep(location);

  const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
  const [tryAgain, setTryAgain] = useState<boolean>(false);
  const [fullAddress, setFullAddress] = useState<AddressInfo | undefined>(undefined);
  const [streetAddress, setStreetAddress] = useState<string | undefined>(undefined);
  const [formattedPhone, setFormattedPhone] = useState<string>();
  const [formattedExt, setFormattedExt] = useState<string>();
  const [formattedZip, setFormattedZip] = useState<string>();
  const [selectedState, setState] = useState<string>('Choose a state');

  useEffect(() => {
    if (!organizationData || Object.keys(organizationData).length === 0) return;
    setStreetAddress(prev =>
      prev === undefined ? (organizationData.streetAddress ?? prev) : prev,
    );
    setState(prev => (prev === 'Choose a state' ? (organizationData.state ?? prev) : prev));
  }, [organizationData]);

  useEffect(() => {
    if (!tryAgain) return;
    const saveErr = saveOrganizationData.error;
    const updateErr = updateOrganizationData.error;
    const isDuplicateName =
      (isAxiosError(saveErr) && saveErr.response?.status === 400) ||
      (isAxiosError(updateErr) && updateErr.response?.status === 400);
    if (isDuplicateName) {
      setToast(
        new Toast({
          message: 'Organization name is already taken. Please use a unique name.',
          severity: 'error',
          open: true,
          autoHideDuration: 3000,
        }),
      );
      setTryAgain(false);
      setLoading(false);
    } else if (
      saveOrganizationData.status === MutationStatus.Complete ||
      updateOrganizationData.status === MutationStatus.Complete
    ) {
      setTryAgain(false);
      const navToNextStep = async () => {
        try {
          if (currentOrganization) {
            await setOrganizationData(currentOrganization);
          }
          await applyRestrictionSelections(restrictionSelections);
          await signUpWizard.init(true);
          const next = signUpWizard.nextStep(location);
          navigate(next.route);
        } catch (err) {
          console.error('Failed to apply pricebook restrictions after org save:', err);
          setToast(
            new Toast({
              message: 'Failed to save pricebook restrictions.',
              severity: 'error',
              open: true,
            }),
          );
        } finally {
          setLoading(false);
        }
      };
      navToNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveOrganizationData, updateOrganizationData, signUpWizard]);

  useEffect(() => {
    if (!userRoles) return;
    if (
      userRoles.length === 0 ||
      (userRoles.length === 1 && userRoles?.[0].role?.includes('ORGANIZATION'))
    ) {
      setIsExistingUser(false);
    } else {
      setIsExistingUser(true);
    }
  }, [userRoles]);

  const validateOrgName = (value: string) => {
    if (value.length > 128) {
      return 'Organization name should not exceed 128 characters.';
    }
    return undefined;
  };

  const validatePhoneNumber = (value: string) => {
    const result = formatPhoneNumber(value);
    setFormattedPhone(result);

    const regex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4}$/im;
    return regex.test(value) ? undefined : 'Invalid Phone Number';
  };

  const validateExtension = (value: string | undefined) => {
    if (!value) return undefined;
    const result = value.replace(/[^0-9]/g, '');
    setFormattedExt(result ? result : '');
  };

  const setOrganizationData = async (orgData: OrganizationData) => {
    await updateSignUpState.action({
      state: { ...orgSignUpState?.state, orgData: JSON.stringify(orgData) },
    });
  };

  const applyRestrictionSelections = async (
    selections: { mode: RestrictionMode; pricebookIds: number[] } | null,
  ) => {
    if (!selections) return;
    if (selections.mode === 'inherit') {
      await revertOrgRestrictions.action();
    } else {
      await saveOrgRestrictions.action(selections.pricebookIds);
    }
    setRestrictionSelections(null);
  };

  const onSubmit = async (values: OrganizationData, form: FormApi<OrganizationData>) => {
    const { name, phoneExtension, city, zip } = values;
    const phoneNumber = trimPhoneNumber(values.phoneNumber ?? '');
    const orgData = {
      streetAddress,
      city,
      state: selectedState,
      zip,
      name,
      phoneNumber,
      phoneExtension,
      type: 'GENERAL',
    };

    const formPristine = form.getState().pristine;
    const nonFormFieldsUnchanged =
      streetAddress === organizationData?.streetAddress &&
      selectedState === organizationData?.state;
    const orgDataUnchanged =
      Boolean(currentOrganization?.id) && formPristine && nonFormFieldsUnchanged;
    const restrictionsUnchanged = !restrictionSelections;

    if (orgDataUnchanged && restrictionsUnchanged) {
      const next = signUpWizard.nextStep(location);
      navigate(next.route);
      return;
    }

    setLoading(true);

    if (orgDataUnchanged) {
      try {
        await applyRestrictionSelections(restrictionSelections);
        await signUpWizard.init(true);
        await updateSignUpState.action({ lastCompletedStep: signUpWizard.getCurrent(location) });
        const next = signUpWizard.nextStep(location);
        navigate(next.route);
      } catch (err) {
        console.error('Failed saving pricebook restrictions:', err);
        setToast(
          new Toast({
            message: 'Failed to save pricebook restrictions.',
            severity: 'error',
            open: true,
          }),
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      if (currentOrganization?.id && currentOrganization?.name === organizationData?.name) {
        await updateOrganizationData.action(orgData);
        await updateSignUpState.action({ lastCompletedStep: signUpWizard.getCurrent(location) });
      } else {
        await saveOrganizationData.action(orgData);
        await createSignUpState.action({
          lastCompletedStep: signUpWizard.getCurrent(location),
        });
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      return;
    }
    setTryAgain(true);
  };

  const onCancel = async () => {
    setRestrictionSelections(null);
    signUpWizard.setSignUpResumed(false);
    if (currentOrganization?.id && currentOrganization.name === organizationData?.name) {
      try {
        setLoading(true);
        await updateSignUpState.action({
          onboardingComplete: false,
        });
        await unselectOrganization.action(currentOrganization.id);
      } finally {
        setLoading(false);
      }
      navigate(back);
      return;
    }
    navigate(back);
  };

  const onAddressSelect = (address: AddressInfo) => {
    setFullAddress(address);
  };

  const isAddressFullyEntered = (values?: OrganizationData) => {
    return (
      streetAddress &&
      values?.city &&
      values?.zip &&
      selectedState &&
      selectedState !== 'Choose a state'
    );
  };

  const validateZip = (value: string | undefined) => {
    if (!value) return undefined;
    const result = value.replace(/[^0-9]/g, '');
    setFormattedZip(result ? result : '');
  };

  if (!googlePlacesKey) {
    return <Loading />;
  }

  return (
    <>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit, values, form }) => {
          const handleAddressSelect = (address: AddressInfo) => {
            onAddressSelect(address);
            form.change('city', address.city);
            form.change('zip', address.zip);
            if (address.zip) setFormattedZip(address.zip);
          };
          return (
            <Column style={{ margin: isMobile || isTablet ? '0 20px' : '0px 100px' }}>
              <Spacer height='xs' />
              <StepsHeader
                activeStep={activeStep}
                totalSteps={totalSteps}
                headerSteps={
                  multipleProductTypesAvailable ? 'onboardingMultipleProducts' : 'onboarding'
                }
              />
              <Spacer height='xs' />
              <Row style={{ alignItems: 'flex-end', gap: '10px' }}>
                <Typography variant='h1' color='secondary'>
                  Step {activeStep}: About your organization
                </Typography>
                {showGearIcon && (
                  <IconButton onClick={() => setPricebookDrawerOpen(true)} sx={{ padding: '2px' }}>
                    <Svgs.IconSettingsDefault />
                  </IconButton>
                )}
              </Row>
              <Spacer height='sm' />
              <Column
                style={{
                  justifyContent: isMobile || isTablet ? '' : 'center',
                  width: isMobile || isTablet ? '100%' : '70%',
                }}>
                {isMobile || isTablet ? (
                  <StackedFormInput<OrganizationData>
                    fieldName='name'
                    label='Organization name'
                    initialValue={organizationData?.name}
                    validate={validateOrgName}
                    required
                  />
                ) : (
                  <HorizontalFormInput<OrganizationData>
                    fieldName='name'
                    label='Organization name'
                    initialValue={organizationData?.name}
                    validate={validateOrgName}
                    rightAlignedKey
                    required
                  />
                )}
                <Spacer height='s25' />
                <PhoneNumberRow
                  formattedPhone={formattedPhone}
                  validatePhoneNumber={validatePhoneNumber}
                  organizationData={organizationData}
                  validateExtension={validateExtension}
                  formattedExt={formattedExt}
                />
                <Spacer height='sm' />
              </Column>

              {isMobile || isTablet ? (
                <Column
                  style={{
                    gap: '20px',
                  }}>
                  <Typography
                    sx={{ flex: '2 0 10px', textAlign: 'left' }}
                    variant='p18SemiBold'
                    color='secondary.dark'>
                    Mailing address
                  </Typography>
                  <Card variant='ghost' color='primary'>
                    <CardContent className='mobileAddressCard'>
                      <Column
                        style={{
                          flexWrap: 'wrap',
                          gap: '20px',
                          alignItems: 'left',
                        }}>
                        <Column style={{ alignItems: 'left' }}>
                          <Typography sx={{ alignItems: 'left' }} variant='body1' color='info.main'>
                            Street address
                          </Typography>
                          <AddressSearchbox
                            organizationData={organizationData}
                            onSubmit={handleAddressSelect}
                            googleKey={googlePlacesKey}
                            fieldName='streetAddress'
                            streetAddress={streetAddress}
                            setStreetAddress={setStreetAddress}
                            setFullAddress={setFullAddress}
                            setState={setState}
                            fullAddress={fullAddress}
                          />
                        </Column>
                        <StackedFormInput<OrganizationData>
                          fieldName='city'
                          label='City'
                          initialValue={organizationData?.city}
                          required
                        />
                        <Column style={{ alignItems: 'left' }}>
                          <Typography sx={{ alignItems: 'left' }} variant='body1' color='info.main'>
                            State
                          </Typography>
                          <DropdownMenu
                            inputStyles={{ alignItems: 'left', width: '100%' }}
                            callback={val => setState(val)}
                            selected={selectedState}
                            options={statesList}
                          />
                        </Column>
                        <StackedFormInput<OrganizationData>
                          fieldName='zip'
                          label='Zip code'
                          initialValue={organizationData?.zip}
                          formattedValue={formattedZip}
                          validate={validateZip}
                          maxLength={5}
                          required
                        />
                      </Column>
                    </CardContent>
                  </Card>
                </Column>
              ) : (
                <Column>
                  <Row style={{ flex: '2 0 10px', justifyContent: 'flex-end', width: '70%' }}>
                    <Typography
                      sx={{ flex: '2 0 10px', textAlign: 'right', paddingRight: '10px' }}
                      variant='p18SemiBold'
                      color='secondary.dark'>
                      Mailing address
                    </Typography>
                    <Row style={{ flex: '2 0 10px' }} />
                  </Row>
                  <Spacer height='xxs' />

                  <Card variant='ghost' color='primary'>
                    <CardContent className='addressCard'>
                      <Row>
                        <Row style={{ gap: '20px', alignItems: 'center', width: '70%' }}>
                          <Typography
                            sx={{
                              flex: '2 0 10px',
                              textAlign: 'right',
                              marginTop: '10px',
                            }}
                            variant='body1'
                            color='info.main'>
                            Street address
                          </Typography>
                          <Row style={{ flex: '2 0 10px', gap: '20px' }}>
                            <AddressSearchbox
                              organizationData={organizationData}
                              onSubmit={handleAddressSelect}
                              googleKey={googlePlacesKey}
                              fieldName='streetAddress'
                              streetAddress={streetAddress}
                              setStreetAddress={setStreetAddress}
                              setFullAddress={setFullAddress}
                              setState={setState}
                              fullAddress={fullAddress}
                            />
                          </Row>
                        </Row>
                      </Row>

                      <Row>
                        <Row style={{ gap: '20px', alignItems: 'center', width: '70%' }}>
                          <Typography
                            sx={{
                              flex: '2 0 10px',
                              textAlign: 'right',
                              marginTop: '10px',
                            }}
                            variant='body1'
                            color='info.main'>
                            City
                          </Typography>
                          <Row style={{ flex: '2 0 10px', gap: '20px' }}>
                            <HorizontalFormInput<OrganizationData>
                              fieldName='city'
                              initialValue={organizationData?.city ?? fullAddress?.city}
                              inputStyle={{
                                minWidth: '90px',
                                flexShrink: 2,
                              }}
                              rightAlignedKey
                              required
                            />
                            <Row
                              style={{
                                flexWrap: 'wrap',
                                gap: '20px',
                                alignItems: 'center',
                                minWidth: '200px',
                              }}>
                              <Typography
                                style={{ flex: '2 0 10px', justifyContent: 'flex-end' }}
                                variant='body1'
                                color='info.main'>
                                State
                              </Typography>
                              <DropdownMenu
                                inputStyles={{ flex: '2 0 10px' }}
                                callback={val => setState(val)}
                                selected={selectedState}
                                options={statesList}
                              />
                            </Row>
                          </Row>
                        </Row>
                        <HorizontalFormInput<OrganizationData>
                          fieldName='zip'
                          label='Zip code'
                          initialValue={organizationData?.zip ?? fullAddress?.zip}
                          formattedValue={formattedZip}
                          inputStyle={{ minWidth: '20px', maxWidth: '110px' }}
                          labelStyle={{ marginTop: '10px' }}
                          validate={validateZip}
                          maxLength={5}
                          rightAlignedKey
                          required
                        />
                        <Spacer width='md' />
                      </Row>
                    </CardContent>
                  </Card>
                </Column>
              )}

              <ButtonFooter
                primaryButton={{
                  label: 'Next: Invite admins',
                  onClick: handleSubmit,
                  disabled: !isAddressFullyEntered(values) || !values?.name || !values.phoneNumber,
                }}
                secondaryButton={{
                  label: 'Cancel',
                  onClick: onCancel,
                  showCondition: isExistingUser,
                }}
              />
            </Column>
          );
        }}
      </Form>
      {showGearIcon && (
        <PricebookRestrictionsDrawer
          drawerOpen={pricebookDrawerOpen}
          setDrawerOpen={setPricebookDrawerOpen}
          adminTier={AdminTierTypeEnum.Organization}
          availablePricebooks={orgAvailablePricebooks}
          parentAvailablePricebooks={teamAvailablePricebooks}
          allPricebookBundles={allPricebookBundles}
          inUsePricebookIds={inUsePricebookIds}
          selectedMode={restrictionSelections?.mode}
          selectedPricebookIds={restrictionSelections?.pricebookIds}
          onValidateSave={validateInUsePricebooks}
          onSave={(mode, pricebookIds) => setRestrictionSelections({ mode, pricebookIds })}
        />
      )}
    </>
  );
};

interface PhoneProps {
  formattedPhone?: string;
  validatePhoneNumber: (x: string) => string | undefined;
  organizationData: OrganizationData | undefined;
  validateExtension: (x: string) => string | undefined;
  formattedExt?: string;
}
const PhoneNumberRow: React.FC<PhoneProps> = ({
  formattedPhone,
  validatePhoneNumber,
  organizationData,
  validateExtension,
  formattedExt,
}) => {
  const { isMobile, isTablet } = useDeviceType();
  return (
    <>
      {isMobile || isTablet ? (
        <Column style={{ justifyContent: 'left' }}>
          <Typography
            sx={{ flex: '2 0 10px', textAlign: 'left', marginTop: '10px' }}
            variant='body1'
            color='info.main'>
            Phone number
          </Typography>
          <FormInput<OrganizationData>
            fieldName='phoneNumber'
            initialValue={organizationData?.phoneNumber}
            formattedValue={formattedPhone}
            validate={validatePhoneNumber}
            maxLength={14}
            required
          />
          <Spacer height='sm' />
          <StackedFormInput<OrganizationData>
            fieldName='phoneExtension'
            label='Ext.'
            initialValue={organizationData?.phoneExtension}
            formattedValue={formattedExt}
            validate={validateExtension}
            maxLength={6}
          />
        </Column>
      ) : (
        <Row style={{ gap: '20px', alignItems: 'center' }}>
          <Typography
            sx={{ flex: '2 0 10px', textAlign: 'right', marginTop: '10px' }}
            variant='body1'
            color='info.main'>
            Phone number
          </Typography>
          <Row style={{ flex: '2 0 10px', gap: '20px' }}>
            <FormInput<OrganizationData>
              fieldName='phoneNumber'
              initialValue={organizationData?.phoneNumber}
              formattedValue={formattedPhone}
              inputStyle={{ minWidth: '90px', flexShrink: '0' }}
              validate={validatePhoneNumber}
              maxLength={14}
              required
            />
            <HorizontalFormInput<OrganizationData>
              fieldName='phoneExtension'
              label='Ext.'
              initialValue={organizationData?.phoneExtension}
              formattedValue={formattedExt}
              labelStyle={{ marginTop: '10px' }}
              inputStyle={{ flexShrink: '5', minWidth: '30px' }}
              validate={validateExtension}
              maxLength={6}
            />
          </Row>
        </Row>
      )}
    </>
  );
};

export default AboutYourOrg;
